import { useEffect, useState } from "react";
import { api, formatApiError, uploadWebsiteImage } from "@/lib/api";
import { toast } from "sonner";

// Constants
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;



export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const load = () => api.get("/gallery/admin/all").then((r) => setItems(r.data));
  const loadCategories = () => api.get("/master/portfolio-categories").then((r) => { const names=r.data.map(x=>x.name); setCategories(names); setCategory((c)=>c || names[0] || ""); });
  useEffect(() => { load(); loadCategories(); }, []);

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const tooLarge = files.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooLarge.length) {
      toast.error(`${tooLarge.length} image(s) are over 10MB. Please choose smaller files.`);
      e.target.value = "";
      return;
    }

    setSelectedFiles(files);
    setPreviews(files.slice(0, 8).map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    })));
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!selectedFiles.length) return toast.error("Please pick one or more images first.");
    setUploading(true);
    let uploaded = 0;
    const failed = [];
    try {
      for (const file of selectedFiles) {
        try {
          const media = await uploadWebsiteImage(file, "portfolio");
          await api.post("/gallery", {
            title: title.trim() || "",
            category,
            image_key: media.image_key || null,
            image_data: media.image_data || null,
          });
          uploaded += 1;
        } catch (err) {
          failed.push(file.name);
        }
      }

      if (uploaded) toast.success(`${uploaded} photo${uploaded === 1 ? "" : "s"} uploaded successfully.`);
      if (failed.length) toast.error(`${failed.length} photo(s) failed to upload: ${failed.join(", ")}`);

      if (uploaded) {
        previews.forEach((p) => URL.revokeObjectURL(p.url));
        setTitle("");
        setPreviews([]);
        setSelectedFiles([]);
        const input = document.querySelector('[data-testid="portfolio-file"]');
        if (input) input.value = "";
        await load();
      }
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateItem = async (item, changes) => {
    try { await api.patch(`/gallery/${item.id}`, changes); toast.success("Portfolio updated"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-portfolio" className="p-10">
      <div className="mb-8">
        <div className="overline mb-2">Website</div>
        <h1 className="font-serif-display text-4xl">Portfolio</h1>
        <p className="text-neutral-500 mt-2">Upload and manage photos shown on the public Portfolio page.</p>
      </div>

      <form
        onSubmit={upload}
        data-testid="portfolio-upload-form"
        className="bg-[#0a0a0c] border border-white/10 p-6 rounded-sm mb-10 grid md:grid-cols-4 gap-6 items-end"
      >
        <div className="md:col-span-1">
          <label className="overline block mb-2">Title <span className="text-neutral-500 normal-case tracking-normal">(optional)</span></label>
          <input
            data-testid="portfolio-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="line-input w-full py-3"
          />
        </div>
        <div>
          <label className="overline block mb-2">Category</label>
          <select
            data-testid="portfolio-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="line-input w-full py-3"
          >
            {categories.map((c) => (
              <option key={c} value={c} className="bg-[#0a0a0c]">{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Images (≤10MB each)</label>
          <input
            type="file"
            accept="image/*"
            multiple
            data-testid="gallery-file"
            onChange={onFile}
            className="text-sm text-neutral-400 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-white file:text-black file:text-xs file:uppercase file:tracking-widest"
          />
        </div>
        <button
          type="submit"
          disabled={uploading}
          data-testid="portfolio-upload-btn"
          className="bg-white text-black py-3 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
        >
          {uploading ? `Uploading ${selectedFiles.length}…` : selectedFiles.length ? `Upload ${selectedFiles.length} Photos` : "Upload Photos"}
        </button>
        {selectedFiles.length > 0 && (
          <div className="md:col-span-4">
            <div className="text-xs text-neutral-400 mb-3">{selectedFiles.length} photo{selectedFiles.length === 1 ? "" : "s"} selected. You can use Ctrl/Shift in the file picker to select multiple photos.</div>
            <div className="flex flex-wrap gap-3">
              {previews.map((p) => (
                <div key={p.url} className="w-24">
                  <img src={p.url} alt={p.name} className="w-24 h-24 object-cover rounded-sm border border-white/10" />
                  <div className="text-[10px] text-neutral-500 truncate mt-1" title={p.name}>{p.name}</div>
                </div>
              ))}
              {selectedFiles.length > previews.length && <div className="text-xs text-neutral-400 self-center">+{selectedFiles.length - previews.length} more</div>}
            </div>
          </div>
        )}
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((i) => (
          <div
            key={i.id}
            data-testid={`portfolio-item-${i.id}`}
            className="group relative border border-white/10 overflow-hidden"
          >
            <div className="aspect-[4/5]">
              <img
                src={i.image_data}
                loading="lazy"
                decoding="async"
                alt={i.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-3 flex justify-between items-center">
              <div className="min-w-0 flex-1 mr-2">
                <input defaultValue={i.title} onBlur={(e) => e.target.value !== i.title && updateItem(i,{title:e.target.value})} className="bg-transparent text-sm text-white w-full border-b border-white/10 py-1" />
                <select value={i.category} onChange={(e)=>updateItem(i,{category:e.target.value})} className="bg-[#0a0a0c] text-xs text-neutral-400 mt-2 w-full">{categories.map(c=><option key={c}>{c}</option>)}</select>
                <label className="text-[10px] uppercase tracking-wider text-neutral-500 flex gap-2 items-center mt-2"><input type="checkbox" checked={i.is_active !== false} onChange={(e)=>updateItem(i,{is_active:e.target.checked})}/> Published</label>
              </div>
              <button
                data-testid={`portfolio-delete-${i.id}`}
                onClick={() => remove(i.id)}
                className="text-xs uppercase text-red-300"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
